import NotImplemented from '@/components/common/not-implemented';
import FlowPlaceholder from './flow-placeholder';
import { useFlowStore } from '@/state/stores/use-flow-store';
import type { FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import { getObjectId, resolveLiveBindingFlowId } from '@/helpers/object.id.helper';
import { Box, Stack } from '@mui/material';
import { TouchApp } from '@mui/icons-material';
import { UpdateSessionSlot } from './update-session-slot';
import type { ReactNode } from 'react';
import { UpdateBreakSlot } from './update-break-slot';
import { GET_PLAYERS_QUERY_KEY } from '@/state/queries/players/use-get-players';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { useFlowModeSwitching } from '../use-flow-mode-switching';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { PerformViewPanel } from './perform-view';
import {
  useGetLiveContextStateQuery,
  GET_LIVE_CONTEXT_STATE_QUERY_KEY,
} from '@/state/queries/player/use-get-live-context-state';
import { usePlayerSocket } from '@/hooks/use-player-socket';
import { getEntityRefFromFlow, resolveSocketEntityFromFlow } from '@/helpers/entity-ref.helper';
import { useQueryClient } from '@tanstack/react-query';
import type { ScheduleInlineDetailsTab } from './schedule-inline-details-tab';
import { shouldRefetchQueriesOnLiveContextInvalidated } from '@/helpers/should-refetch-on-live-context-invalidated.helper';

/** Schedule tab content: slot edit when selected, placeholder when no slot. */
export default function EditDetails({
  selectedSlot,
  slotTitle,
  onSlotDeleted,
  onSessionPublishSuccess,
  eventSlotIdParam,
  inlineScheduleDetailsTab,
}: {
  selectedSlot: FlowSlotDto | undefined;
  slotTitle: string;
  onSlotDeleted?: () => void;
  onSessionPublishSuccess?: () => void;
  eventSlotIdParam?: string | null;
  /** Narrow inline schedule: which session widget is visible under the row */
  inlineScheduleDetailsTab?: ScheduleInlineDetailsTab;
}) {
  const { t } = useTranslation();
  const flow = useFlowStore((s) => s.flow);
  const isWideScreen = useBreakpointUp(860);
  const [searchParams] = useSearchParams();
  const eventSlotIdFromUrl = searchParams.get('eventSlotId');
  const eventSlotIdResolved = eventSlotIdParam ?? eventSlotIdFromUrl;

  const { currentMode } = useFlowModeSwitching({
    flowId: flow?.origin ? getObjectId(flow.origin) : flow?.id,
    flow,
  });

  const isPerformMode = currentMode === 'PERFORM';

  const queryClient = useQueryClient();
  const bindingFlowId = flow ? resolveLiveBindingFlowId(flow, eventSlotIdResolved ?? undefined) : undefined;
  const entityRef = bindingFlowId ? getEntityRefFromFlow(bindingFlowId, flow?.liveContext) : undefined;
  const socketEntity = flow ? resolveSocketEntityFromFlow(flow, eventSlotIdResolved ?? undefined) : undefined;
  useGetLiveContextStateQuery(entityRef, {
    enabled: isPerformMode && !!entityRef && !!flow?.liveContext?.isRunning,
  });
  usePlayerSocket(
    socketEntity?.entityId ?? '',
    socketEntity?.entityType ?? 'Flow',
    {
      onPlayersInvalidated: () => {
        queryClient.invalidateQueries({ queryKey: [GET_PLAYERS_QUERY_KEY] });
      },
      onLiveContextInvalidated: (_entityId, _entityType, meta) => {
        if (
          !shouldRefetchQueriesOnLiveContextInvalidated({
            liveIsRunning: Boolean(flow?.liveContext?.isRunning),
            isRunningFromEvent: meta?.isRunning,
          })
        ) {
          return;
        }
        if (entityRef) queryClient.invalidateQueries({ queryKey: [GET_LIVE_CONTEXT_STATE_QUERY_KEY, entityRef] });
      },
    },
    isPerformMode && !!socketEntity?.entityId,
  );

  if (!flow) return null;

  const renderSlotBody = (slot: FlowSlotDto): ReactNode => {
    switch (slot.contentType) {
      case 'Session':
        return (
          <UpdateSessionSlot
            slot={slot as FlowSlotDto<'Session'>}
            onSessionPublishSuccess={onSessionPublishSuccess}
            showActorsAndMedia={false}
            inlineDetailsTab={inlineScheduleDetailsTab}
          />
        );
      case 'Break':
        return (
          <UpdateBreakSlot
            slot={slot as FlowSlotDto<'Break'>}
            slotTitle={slotTitle}
            onSlotDeleted={onSlotDeleted}
            showMediaWidget={false}
          />
        );
      default:
        return <NotImplemented label={slot.title ?? 'Slot edit'} />;
    }
  };

  if (selectedSlot) {
    if (isPerformMode) {
      return (
        <Box width="100%">
          <PerformViewPanel selectedSlot={selectedSlot} eventSlotIdParam={eventSlotIdResolved} />
        </Box>
      );
    }
    return (
      <Box width="100%">
        <Stack spacing={2} sx={{ mt: isWideScreen ? 0 : 1 }}>
          {renderSlotBody(selectedSlot)}
        </Stack>
      </Box>
    );
  }

  return (
    <Box width="100%">
      <FlowPlaceholder
        title={t('flows.detail.editDetails.scheduleTitle')}
        subtitle={t('flows.detail.editDetails.scheduleEmptyHint')}
        icon={<TouchApp sx={{ fontSize: 36, color: 'action.disabled' }} />}
      />
    </Box>
  );
}
